import { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import { toast } from 'react-toastify';
import '../assets/style/contact.scss';
import '../assets/style/scroll.scss';
import Fireworks from './ConfettiContainer';
import logo from '../assets/images/SR-Dev-Logo-Crown-Cropped.png';
import LabelInput from './LabelInput';

const Contact = () => {
  const recaptchaRef = useRef(null);
  const [triggerAnimation, setTriggerAnimation] = useState(false);
  const [inputValues, setInputValues] = useState({
    contactFullName: '',
    contactEmail: '',
    contactSubject: '',
    contactMessage: '',
  });

  const testEmailRegex = (email) => {
    const emailRegex = /^[\w.-]{3,}@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}$/;
    const isValidEmail = emailRegex.test(email);

    return isValidEmail;
  };

  const handleChange = (e, inputId) => {
    const { value } = e.target;
    setInputValues((prevInputValues) => ({
      ...prevInputValues,
      [inputId]: value,
    }));
  };

  const notifyEmail = (message, type) => {
    const toastId = 'my-custom-toast-IV';

    if (!toast.isActive(toastId)) {
      const toastOptions = {
        toastId,
        position: 'top-center',
        autoClose: 2500,
        hideProgressBar: false,
        newestOnTop: false,
        closeOnClick: true,
        rtl: false,
        theme: 'light',
      };

      switch (type) {
        case 'success':
          toast.success(message, { ...toastOptions });
          break;

        case 'warn':
          toast.warn(message, { ...toastOptions });
          break;

        case 'error':
          toast.error(message, { ...toastOptions });
          break;

        default:
          toast.info(message, { ...toastOptions });
          break;
      }
    }
  };

  const templateParams = {
    contactFullName: inputValues.contactFullName,
    contactEmail: inputValues.contactEmail,
    contactSubject: inputValues.contactSubject,
    contactMessage: inputValues.contactMessage,
  };

  const sendEmail = () => {
    emailjs
      .send(process.env.REACT_APP_EMAILJS_SERVICE_KEY,
        process.env.REACT_APP_EMAILJS_TEMPLATE_KEY,
        templateParams, {
          publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
        })
      .then(
        () => {
          notifyEmail("Your email has been successfully sent. We'll get back to you as soon as possible, please be patient.", 'success');
          setInputValues({
            contactFullName: '',
            contactEmail: '',
            contactSubject: '',
            contactMessage: '',
          });
          setTriggerAnimation(true);
          setTimeout(() => {
            setTriggerAnimation(false);
          }, 2000);
        },
        (err) => {
          notifyEmail(err, 'error');
        },
      );
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    const {
      contactFullName, contactEmail, contactSubject, contactMessage,
    } = inputValues;

    if (!contactFullName.trim()) {
      notifyEmail('Full name is required.', 'error');
      return; // Stop further execution if validation fails
    }
    if (!contactEmail.trim()) {
      notifyEmail('Email is required.', 'error');
      return;
    }

    if (!testEmailRegex(contactEmail)) {
      notifyEmail('Please input valid email', 'warn');
      return;
    }

    if (!contactSubject.trim()) {
      notifyEmail('Subject is required.', 'error');
      return;
    }

    if (!contactMessage.trim()) {
      notifyEmail('Message is required.', 'error');
      return;
    }

    // Get the reCAPTCHA token
    const token = window.grecaptcha.getResponse();
    window.grecaptcha.reset();

    if (token) {
      templateParams['g-recaptcha-response'] = token;
      sendEmail();
    } else {
      notifyEmail('Please complete the reCAPTCHA challenge.', 'error');
    }
  };

  return (
    <div className="contact-wrapper">
      <Fireworks trigger={triggerAnimation} />
      <div className="contact-wrapper--col-one">
        <div className="aesthetic-top-one" />
        <div className="aesthetic-top-two" />
        <div className="aesthetic-top-three" />
        <div className="aesthetic-vertical-one" />
        <div className="aesthetic-vertical-two" />
        <img className="contact-wrapper--col-one__contact-logo" src={logo} alt="Contact logo" />
        <div className="contact-wrapper--col-one-bg" />
        <div className="aesthetic-bottom-one" />
        <div className="aesthetic-bottom-two" />
        <div className="aesthetic-bottom-three" />
      </div>
      <div className="contact-wrapper--col-two">
        <h2 className="contact-wrapper--col-two__header">Let&apos;s get in touch!</h2>
        <form className="contact-wrapper--col-two__form" method="POST" onSubmit={handleFormSubmit}>
          <div className="contact-wrapper--col-two__labels_wrapper">
            <LabelInput
              label="Full name"
              placeholder='Your first name and last name; (e.g., "John Doe")'
              id="contactFullName"
              value={inputValues.contactFullName}
              onChange={(e) => handleChange(e, 'contactFullName')}
              onFocus={() => { }}
              onBlur={() => { }}
            />

            <LabelInput
              label="E-mail"
              placeholder='Your e-mail address; (e.g., "johndoe@mail.com")'
              id="contactEmail"
              value={inputValues.contactEmail}
              onChange={(e) => handleChange(e, 'contactEmail')}
              onFocus={() => { }}
              onBlur={() => { }}
            />

            <LabelInput
              label="Subject"
              placeholder='Your e-mail title; (e.g., "Meeting request..." )'
              id="contactSubject"
              value={inputValues.contactSubject}
              onChange={(e) => handleChange(e, 'contactSubject')}
              onFocus={() => { }}
              onBlur={() => { }}
            />

            <LabelInput
              inputType="textarea"
              label="Message"
              placeholder='Type your message; (e.g., "Hello, I have a...")'
              id="contactMessage"
              value={inputValues.contactMessage}
              onChange={(e) => handleChange(e, 'contactMessage')}
              onFocus={() => { }}
              onBlur={() => { }}
            />
          </div>
          <button type="submit" className="btn btn-1 contact-btn">
            <svg>
              <rect x="0" y="0" fill="none" width="100%" height="100%" />
            </svg>
            Submit
          </button>
          <div className="g-recaptcha" ref={recaptchaRef} data-sitekey={process.env.REACT_APP_RECAPTCHA_SECRET_KEY} />
        </form>
      </div>
    </div>
  );
};

export default Contact;
